// Create a new file: /components/workflow/RestructureNodeModal.jsx

import React, {
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import { produce } from 'immer';
import toast from 'react-hot-toast';
import { Edit, Link2 } from 'lucide-react';

import SchemaViewer from '../NewWorkflow/shared/SchemaViewer';
import SchemaBuilderModal from '../NewWorkflow/modals/SchemaBuilderModal';
import {
  fieldsToSchema,
  schemaToFields,
} from '../NewWorkflow/utils/schemaUtils';

// --- HELPERS (SCOPED TO THIS FILE) ---
const findFieldPathById = (fields, id, currentPath = []) => {
  for (const field of fields) {
    const path = [...currentPath, field.fieldName];
    if (field.id === id) return path.join('.');
    if (field.children?.length) {
      const foundPath = findFieldPathById(field.children, id, path);
      if (foundPath) return foundPath;
    }
  }
  return null;
};

const generateId = () =>
  `field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

const deepCloneField = (field) => {
  const newField = { ...field, id: generateId() };
  if (field.children && field.children.length > 0) {
    newField.children = field.children.map(deepCloneField);
  }
  return newField;
};

// const schemaToFields = (schema, prefix) => {
//     if (!schema || !schema.properties) return [];
//     return Object.entries(schema.properties).map(([key, value]) => ({
//         id: `${prefix}${key}`,
//         fieldName: key,
//         dataType: value.type || 'string',
//         description: value.description,
//         children: (value.type === 'object' && value.properties) ? schemaToFields(value, `${prefix}${key}.`) : [],
//         arrayItemType: value.items?.type || 'String',
//     }));
// };

// // --- *** NEW & CRITICAL ***: Converts editable fields array to JSON Schema ---
// const fieldsToSchema = (fields, modelName = 'Output') => {
//     const convert = (fieldList) => {
//         const properties = {};
//         const required = [];

//         fieldList.forEach(field => {
//             if (!field.fieldName) return;
//             let schemaDef = { description: field.description };

//             const dataType = field.dataType.toLowerCase();

//             switch (dataType) {
//                 case 'string': case 'number': case 'boolean':
//                     schemaDef.type = dataType;
//                     break;
//                 case 'object':
//                     schemaDef.type = 'object';
//                     const nested = convert(field.children || []);
//                     schemaDef.properties = nested.properties;
//                     if (nested.required.length > 0) schemaDef.required = nested.required;
//                     break;
//                 case 'array':
//                     schemaDef.type = 'array';
//                     const itemType = field.arrayItemType?.toLowerCase() || 'string';
//                     if (itemType === 'object') {
//                         const nestedArray = convert(field.children || []);
//                         schemaDef.items = {
//                             type: 'object',
//                             properties: nestedArray.properties,
//                         };
//                         if (nestedArray.required.length > 0) {
//                             schemaDef.items.required = nestedArray.required;
//                         }
//                     } else {
//                         schemaDef.items = { type: itemType };
//                     }
//                     break;
//                 default:
//                     schemaDef.type = 'string';
//             }
//             properties[field.fieldName] = schemaDef;
//             if (field.required) required.push(field.fieldName);
//         });
//         return { properties, required };
//     };

//     const root = convert(fields);
//     return {
//         title: modelName,
//         type: 'object',
//         properties: root.properties,
//         ...(root.required.length > 0 && { required: root.required }),
//     };
// };

const RestructureNodeModal = ({
  mode,
  node,
  nodes,
  edges,
  onSave,
  onClose,
  onDelete,
  globalState,
}) => {
  const [outputFields, setOutputFields] = useState(
    node?.data?.outputFields || [],
  );
  const [mappings, setMappings] = useState(node?.data?.mappings || {});
  // ... (rest of the state from the reference StructureNodeModal)
  const [selectedInputField, setSelectedInputField] = useState(null);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);

  const containerRef = useRef(null);
  const elementRefs = useRef(new Map());
  const [lines, setLines] = useState([]);

  const inputRootFields = useMemo(() => {
    // return edges
    //     .filter(edge => edge.target === node.id)
    //     .map(edge => nodes.find(n => n.id === edge.source))
    //     .filter(Boolean)
    //     .map(parentNode => {
    //         let schema;
    //         // --- THIS IS THE CRITICAL LOGIC ---
    //         // Check if the parent has a defined JSON output.
    //         if (parentNode.data.jsonOutput && parentNode.data.jsonOutputSchema?.properties) {
    //             schema = parentNode.data.jsonOutputSchema;
    //         } else {
    //             // If not, create a default schema for its simple string output.
    //             schema = {
    //                 title: 'Simple Output',
    //                 type: 'object',
    //                 properties: {
    //                     'output': {
    //                         type: 'string',
    //                         description: `The plain text output from "${parentNode.data.label || parentNode.type}"`
    //                     }
    //                 }
    //             };
    //         }
    //         // --- END OF CRITICAL LOGIC ---

    //         if (!schema) return null;

    //         const inputKey = `input_${parentNode.id}`;
    //         return {
    //             id: inputKey,
    //             fieldName: parentNode.data.label || 'Input Node',
    //             dataType: 'Object',
    //             children: schemaToFields(schema, ''),
    //         };
    //     }).filter(Boolean);
    const connectedInputNodes = edges
      .filter((edge) => edge.target === node.id)
      .map((edge) => nodes.find((n) => n.id === edge.source))
      .filter(Boolean)
      .filter((parentNode) => parentNode.type !== 'loopBreak') // ⭐ ADD THIS LINE
      .map((parentNode) => {
        let schema;
        if (
          parentNode.data.jsonOutput &&
          parentNode.data.jsonOutputSchema?.properties
        ) {
          schema = parentNode.data.jsonOutputSchema;
        } else {
          schema = {
            title: 'Simple Output',
            type: 'object',
            properties: {
              output: {
                type: 'string',
                description: `Output from "${parentNode.data.label || parentNode.type}"`,
              },
            },
          };
        }
        return {
          id: `input_${parentNode.id}`,
          fieldName: parentNode.data.label || 'Input Node',
          dataType: 'Object',
          children: schemaToFields(schema, ''),
        };
      })
      .filter(Boolean);

    // ⭐ 2. Create a "virtual node" for the Global State if it exists
    const globalStateNode = [];
    if (globalState && globalState.fields.length > 0) {
      const globalSchema = fieldsToSchema(globalState.fields, 'globalState');
      globalStateNode.push({
        id: 'globalState', // Special, reserved ID
        fieldName: 'Global State',
        dataType: 'Object',
        children: schemaToFields(globalSchema, ''),
      });
    }

    // ⭐ 3. Combine the Global State with the other input nodes
    return [...globalStateNode, ...connectedInputNodes];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, nodes, edges]);

  // ... (Paste the entire rest of the component logic from the reference StructureNodeModal.js)
  // No other changes are needed in this component.

  const outputRootFields = useMemo(
    () => [
      {
        id: 'output',
        fieldName: 'Output',
        dataType: 'Object',
        children: outputFields,
      },
    ],
    [outputFields],
  );

  const registerRef = useCallback((id, el) => {
    if (el) elementRefs.current.set(id, el);
    else elementRefs.current.delete(id);
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      const newLines = Object.entries(mappings)
        .map(([outputFieldId, inputPathArray]) => {
          const inputPath = inputPathArray.join('.');
          const outputFieldPath = findFieldPathById(
            outputRootFields,
            outputFieldId,
            [],
          );

          const startEl = elementRefs.current.get(`handle-source-${inputPath}`);
          const endEl = elementRefs.current.get(
            `handle-target-${outputFieldPath}`,
          );

          if (startEl && endEl) {
            const cRect = container.getBoundingClientRect();
            const sRect = startEl.getBoundingClientRect();
            const eRect = endEl.getBoundingClientRect();
            const d = `M ${sRect.right - cRect.left} ${sRect.top + sRect.height / 2 - cRect.top} C ${sRect.right - cRect.left + 60} ${sRect.top + sRect.height / 2 - cRect.top}, ${eRect.left - cRect.left - 60} ${eRect.top + eRect.height / 2 - cRect.top}, ${eRect.left - cRect.left} ${eRect.top + eRect.height / 2 - cRect.top}`;
            return { id: `${inputPath}-${outputFieldId}`, d };
          }
          return null;
        })
        .filter(Boolean);
      setLines(newLines);
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [mappings, outputRootFields]);

  const handleInputFieldClick = (path, field) => {
    setSelectedInputField({ path: path.split('.'), field });
    toast.success(`Selected input: ${field.fieldName}`);
  };

  const handleOutputFieldClick = (path, field) => {
    if (mappings[field.id]) {
      setMappings(
        produce((draft) => {
          delete draft[field.id];
        }),
      );
      toast.success(`Unmapped ${field.fieldName}`);
      return;
    }
    if (!selectedInputField) {
      toast.error('Please select an input field first.');
      return;
    }
    if (
      selectedInputField.field.dataType.toLowerCase() !==
      field.dataType.toLowerCase()
    ) {
      toast.error(
        `Type mismatch: Can't map ${selectedInputField.field.dataType} to ${field.dataType}.`,
      );
      return;
    }
    setMappings(
      produce((draft) => {
        draft[field.id] = selectedInputField.path;
      }),
    );
    setSelectedInputField(null);
  };

  const handlePassThroughClick = (field, path) => {
    const newField = deepCloneField(field);
    if (outputFields.some((f) => f.fieldName === newField.fieldName)) {
      toast.error(`Field "${newField.fieldName}" already exists in output.`);
      return;
    }
    setOutputFields(
      produce((draft) => {
        draft.push(newField);
      }),
    );
    setMappings(
      produce((draft) => {
        draft[newField.id] = path.split('.');
      }),
    );
    toast.success(`Added & mapped "${newField.fieldName}" to output.`);
  };

  const handleSchemaSave = ({ fields }) => {
    setOutputFields(fields);
    const findAllIds = (flds) =>
      flds.reduce((acc, f) => {
        acc.add(f.id);
        if (f.children?.length)
          findAllIds(f.children).forEach((id) => acc.add(id));
        return acc;
      }, new Set());
    const validFieldIds = findAllIds(fields);
    setMappings(
      produce((draft) => {
        for (const key in draft) {
          if (!validFieldIds.has(key)) delete draft[key];
        }
      }),
    );
  };

  const handleSaveAndClose = () => {
    const finalOutputSchema = fieldsToSchema(
      outputFields,
      'RestructuredOutput',
    );

    onSave({
      ...node,
      data: {
        ...node.data,
        label: `Restructure (${Object.keys(mappings).length} mappings)`,
        outputFields,
        jsonOutputSchema: finalOutputSchema,
        jsonOutput: true, // This node always produces a structured output
        mappings,
      },
    });
    onClose();
  };

  // ... (rest of the component JSX)

  return (
    <>
      {/* eslint-disable-next-line */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
        onClick={onClose}
      >
        {/* eslint-disable-next-line */}
        <div
          className="flex h-[90vh] w-full max-w-7xl flex-col rounded-xl border border-slate-700 bg-slate-800 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-700 p-4">
            <h2 className="flex items-center gap-2 text-xl font-semibold text-white">
              <Link2 /> Schema Mapper
            </h2>
            <button
              onClick={() => setIsBuilderOpen(true)}
              className="flex items-center gap-2 rounded-lg bg-slate-700 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-600"
            >
              <Edit size={16} />
              <span>Edit Output Schema</span>
            </button>
          </div>

          <div
            className="relative grid flex-grow grid-cols-2 gap-4 overflow-hidden p-4"
            ref={containerRef}
          >
            <svg className="pointer-events-none absolute left-0 top-0 z-10 h-full w-full">
              {lines.map((line) => (
                <path
                  key={line.id}
                  d={line.d}
                  stroke="#6366f1"
                  strokeWidth="2"
                  fill="none"
                  className="opacity-75"
                />
              ))}
            </svg>

            <div className="flex flex-col gap-2 overflow-y-auto pr-2">
              <h3 className="text-lg font-semibold text-slate-200">
                Input (Click to Select)
              </h3>
              <SchemaViewer
                rootFields={inputRootFields}
                onFieldClick={handleInputFieldClick}
                onPassThroughClick={handlePassThroughClick}
                selectedPath={selectedInputField?.path.join('.')}
                registerRef={registerRef}
                mappings={mappings}
              />
            </div>
            <div className="flex flex-col gap-2 overflow-y-auto pl-2">
              <h3 className="text-lg font-semibold text-slate-200">
                Output (Click to Map)
              </h3>
              <SchemaViewer
                rootFields={outputRootFields}
                onFieldClick={handleOutputFieldClick}
                isOutput={true}
                registerRef={registerRef}
                mappings={mappings}
              />
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center justify-between rounded-b-lg border-t border-zinc-700 bg-zinc-800/50 p-4">
            {mode === 'edit' && node?.id && (
              <button
                onClick={() => onDelete(node.id)}
                className="rounded-md bg-red-900/50 px-4 py-2 text-sm font-semibold text-red-400 transition-colors hover:bg-red-900/80"
              >
                Delete Node
              </button>
            )}
            <div className="flex-grow" />
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="rounded-md bg-zinc-700 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-zinc-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAndClose}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-500"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      <SchemaBuilderModal
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        onSave={handleSchemaSave}
        initialFields={outputFields}
      />
    </>
  );
};

export default RestructureNodeModal;
